import React, { useState, useEffect, useMemo } from 'react';
import styles from './TrainingDashboard.module.css';

export interface CorrectionRecord {
  id: number;
  timestamp: string;
  correctedBy: string;
  correctedByRole: string;
  skill: string;
  jobId?: string;
  jobType?: string;
  originalOutput: string;
  correctedOutput: string;
  correctionType: string;
  severity: 'minor' | 'moderate' | 'major' | 'critical';
  notes?: string;
}

export interface PracticeRecord {
  id: number;
  title: string;
  description: string;
  category: string;
  subcategory?: string;
  priority: string;
  taughtBy: string;
  createdAt: string;
  active: boolean;
  confidence: number;
  timesApplied: number;
  timesConfirmed: number;
}

export interface TrainingDashboardProps {
  corrections: CorrectionRecord[];
  practices: PracticeRecord[];
  onRefresh?: () => void;
}

type DateRange = 'all' | '7d' | '30d' | '90d';
type CategoryFilter = 'all' | 'pricing' | 'scope' | 'product' | 'installation' | 'accounting';

const CATEGORY_BAR_CLASS: Record<string, string> = {
  pricing: styles.barPricing,
  scope: styles.barScope,
  product: styles.barProduct,
  installation: styles.barInstallation,
  accounting: styles.barAccounting,
};

const ACCURACY_COLORS: Record<string, string> = {
  pricing: 'var(--amber)',
  scope: 'var(--blue)',
  product: 'var(--green)',
  installation: 'var(--purple)',
  accounting: 'var(--red)',
};

function isWithinRange(timestamp: string, range: DateRange): boolean {
  if (range === 'all') return true;
  const now = Date.now();
  const t = new Date(timestamp).getTime();
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  return now - t <= days * 24 * 60 * 60 * 1000;
}

function formatRelativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export const TrainingDashboard: React.FC<TrainingDashboardProps> = ({
  corrections,
  practices,
  onRefresh,
}) => {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');

  useEffect(() => {
    onRefresh?.();
  }, [onRefresh]);

  const filteredCorrections = useMemo(() => {
    return corrections.filter((c) => {
      if (!isWithinRange(c.timestamp, dateRange)) return false;
      if (categoryFilter !== 'all' && c.correctionType !== categoryFilter) return false;
      return true;
    });
  }, [corrections, dateRange, categoryFilter]);

  const correctionsByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredCorrections.forEach((c) => {
      const cat = c.correctionType || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [filteredCorrections]);

  const maxCategoryCount = useMemo(() => {
    return Math.max(1, ...Object.values(correctionsByCategory));
  }, [correctionsByCategory]);

  const activePracticesCount = useMemo(() => {
    return practices.filter((p) => p.active).length;
  }, [practices]);

  const accuracyByCategory = useMemo(() => {
    const categories = ['pricing', 'scope', 'product', 'installation', 'accounting'];
    return categories.map((cat) => {
      const catPractices = practices.filter((p) => p.category === cat && p.active);
      if (catPractices.length === 0) return { category: cat, accuracy: 0, hasData: false };
      const totalApplied = catPractices.reduce((sum, p) => sum + p.timesApplied, 0);
      const totalConfirmed = catPractices.reduce((sum, p) => sum + p.timesConfirmed, 0);
      const accuracy = totalApplied > 0 ? Math.round((totalConfirmed / totalApplied) * 100) : 0;
      return { category: cat, accuracy, hasData: true };
    });
  }, [practices]);

  const recentCorrections = useMemo(() => {
    return [...filteredCorrections]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 8);
  }, [filteredCorrections]);

  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboardHeader}>
        <h2 className={styles.dashboardTitle}>Learning Analytics</h2>
        <div className={styles.filters}>
          <select
            className={styles.filterSelect}
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <select
            className={styles.filterSelect}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
          >
            <option value="all">All Categories</option>
            <option value="pricing">Pricing</option>
            <option value="scope">Scope</option>
            <option value="product">Product</option>
            <option value="installation">Installation</option>
            <option value="accounting">Accounting</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.statAccent}`}>
          <span className={styles.statValue}>{filteredCorrections.length}</span>
          <span className={styles.statLabel}>Total Corrections</span>
        </div>
        <div className={`${styles.statCard} ${styles.statGreen}`}>
          <span className={styles.statValue}>{activePracticesCount}</span>
          <span className={styles.statLabel}>Active Practices</span>
        </div>
        <div className={`${styles.statCard} ${styles.statBlue}`}>
          <span className={styles.statValue}>
            {practices.reduce((sum, p) => sum + p.timesApplied, 0)}
          </span>
          <span className={styles.statLabel}>Times Applied</span>
        </div>
        <div className={`${styles.statCard} ${styles.statPurple}`}>
          <span className={styles.statValue}>
            {Object.keys(correctionsByCategory).length}
          </span>
          <span className={styles.statLabel}>Categories Touched</span>
        </div>
      </div>

      {/* Corrections by Category */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Corrections by Category</h3>
        <div className={styles.categoryGrid}>
          {Object.entries(correctionsByCategory).map(([cat, count]) => (
            <div className={styles.categoryCard} key={cat}>
              <span className={styles.categoryName}>{cat}</span>
              <span className={styles.categoryCount}>{count}</span>
              <div className={styles.categoryBar}>
                <div
                  className={`${styles.categoryBarFill} ${CATEGORY_BAR_CLASS[cat] || styles.barPricing}`}
                  style={{ width: `${(count / maxCategoryCount) * 100}%` }}
                />
              </div>
            </div>
          ))}
          {Object.keys(correctionsByCategory).length === 0 && (
            <div className={styles.emptyState}>No corrections in this range</div>
          )}
        </div>
      </div>

      {/* Accuracy Trend */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Accuracy by Category</h3>
        <div className={styles.accuracySection}>
          {accuracyByCategory.map(({ category, accuracy, hasData }) => (
            <div className={styles.accuracyRow} key={category}>
              <span className={styles.accuracyLabel}>{category}</span>
              <div className={styles.accuracyBarContainer}>
                <div
                  className={styles.accuracyBarFill}
                  style={{
                    width: hasData ? `${accuracy}%` : '0%',
                    background: ACCURACY_COLORS[category] || 'var(--amber)',
                  }}
                />
              </div>
              <span className={styles.accuracyValue}>
                {hasData ? `${accuracy}%` : '--'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Corrections */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Recent Corrections</h3>
        <div className={styles.recentList}>
          {recentCorrections.map((c) => (
            <div className={styles.recentItem} key={c.id}>
              <span className={styles.recentSkill}>{c.skill}</span>
              <span className={styles.recentText}>
                {c.originalOutput.slice(0, 60)}
                {c.originalOutput.length > 60 ? '...' : ''}
                {' -> '}
                {c.correctedOutput.slice(0, 60)}
                {c.correctedOutput.length > 60 ? '...' : ''}
              </span>
              <span className={styles.recentTime}>
                {formatRelativeTime(c.timestamp)}
              </span>
            </div>
          ))}
          {recentCorrections.length === 0 && (
            <div className={styles.emptyState}>No recent corrections</div>
          )}
        </div>
      </div>
    </div>
  );
};
